/**
 * Provenance badges shared by every view that shows a question or a card
 * (integration pass 2).
 *
 * `SourceBadge` turns a question's `source` into one short, unobtrusive label:
 *
 * | `source.kind`   | Swedish                      | English                        |
 * | --------------- | ---------------------------- | ------------------------------ |
 * | `exam`          | Tenta HT24 · uppg. 5         | Exam HT24 · problem 5          |
 * | `homework`      | Hemuppgift 3 (HT2025)        | Hand-in 3 (HT2025)             |
 * | `practice-exam` | Övningstenta PE-03 · uppg. 5 | Practice exam PE-03 · problem 5|
 * | `testbank`      | Testbank                     | Test bank                      |
 * | `original`      | — (nothing is rendered)      | —                              |
 *
 * `TranslationPendingBadge` is the companion for `translationPending` content:
 * the test-bank import ships Swedish only, with `en` a copy of `sv`, so an
 * English-reading student is told rather than left wondering. It renders
 * nothing while the active language is Swedish — there the text is not a
 * translation gap, it is simply the source.
 *
 * Both live in `client/src/components` rather than in a feature because
 * practice, the exam simulator, flashcards and the library archive all need
 * the identical label; the strings are core dictionary keys (`source.*`) for
 * the same reason.
 */
import type { PublicQuestion } from "@shared/types";
import { useLang, useT } from "@client/i18n";
import { Badge } from "./ui";

export type QuestionSource = PublicQuestion["source"];

export interface SourceBadgeProps {
  source: QuestionSource | undefined;
  className?: string;
}

/** The label for a source, or `null` for `original` (and for anything unknown). */
export function sourceLabelKey(source: QuestionSource | undefined): string | null {
  switch (source?.kind) {
    case "exam":
      return "source.exam";
    case "homework":
      return "source.homework";
    case "practice-exam":
      return "source.practiceExam";
    case "testbank":
      return "source.testbank";
    default:
      return null;
  }
}

function sourceVars(source: QuestionSource): Record<string, string | number> {
  switch (source.kind) {
    case "exam":
      return { exam: source.exam, number: source.number };
    case "homework":
      return { term: source.term, number: source.number, problem: source.problem };
    case "practice-exam":
      return { paper: source.paper, number: source.number };
    default:
      return {};
  }
}

export function SourceBadge({ source, className }: SourceBadgeProps) {
  const t = useT();
  const key = sourceLabelKey(source);
  if (!key || !source) return null;
  return (
    <Badge tone="neutral" className={className}>
      {t(key, sourceVars(source))}
    </Badge>
  );
}

export interface TranslationPendingBadgeProps {
  /** The item's `translationPending` flag; `false`/`undefined` renders nothing. */
  pending: boolean | undefined;
  className?: string;
}

export function TranslationPendingBadge({ pending, className }: TranslationPendingBadgeProps) {
  const t = useT();
  const { lang } = useLang();
  if (!pending || lang !== "en") return null;
  return (
    <Badge tone="warning" className={className}>
      {t("source.translationPending")}
    </Badge>
  );
}
